import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { from, map, of, switchMap, tap, throwError } from 'rxjs';
import {
  heroArrowRightOnRectangle,
  heroBuildingOffice2,
  heroCheck,
  heroKey,
  heroPencil,
  heroPlus,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { FilesApiService } from '../../../core/api/files-api.service';
import { AuthStore } from '../../../core/auth/auth.store';
import {
  ToastrService,
  UiAvatarButton,
  UiBadge,
  UiButton,
  UiConfirmDialogService,
  UiDataTable,
  UiDataTableActions,
  UiDataTableCell,
  UiDataTableEmptyCell,
  UiDataTableHead,
  UiDataTableHeaderCell,
  UiDataTableRow,
  UiDataTableShell,
  UiEmptyState,
  UiFileInput,
  UiHint,
  UiInput,
  UiPageHeader,
  UiPanelCard,
  UiRecordRowIcon,
  UiSectionHeader,
} from '../../../shared/ui';
import { SettingsCoreApiService, type CoreMeBusiness, type CoreMeResponse } from '../settings-core-api.service';

const AVATAR_CONTENT_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

@Component({
  selector: 'app-profile-settings-page',
  imports: [
    NgIcon,
    ReactiveFormsModule,
    RouterLink,
    UiAvatarButton,
    UiBadge,
    UiButton,
    UiDataTable,
    UiDataTableActions,
    UiDataTableCell,
    UiDataTableEmptyCell,
    UiDataTableHead,
    UiDataTableHeaderCell,
    UiDataTableRow,
    UiDataTableShell,
    UiEmptyState,
    UiFileInput,
    UiHint,
    UiInput,
    UiPageHeader,
    UiPanelCard,
    UiRecordRowIcon,
    UiSectionHeader,
  ],
  providers: [
    provideIcons({
      heroArrowRightOnRectangle,
      heroBuildingOffice2,
      heroCheck,
      heroKey,
      heroPencil,
      heroPlus,
      heroXMark,
    }),
  ],
  templateUrl: './profile-settings.page.html',
  styleUrl: './profile-settings.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSettingsPage {
  private readonly api = inject(SettingsCoreApiService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filesApi = inject(FilesApiService);
  private readonly fb = inject(FormBuilder);
  private readonly confirm = inject(UiConfirmDialogService);
  private readonly toastr = inject(ToastrService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly uploadingAvatar = signal(false);
  readonly requestingPasswordReset = signal(false);
  readonly editingName = signal(false);
  readonly switchingBusinessId = signal<string | null>(null);
  readonly me = signal<CoreMeResponse | null>(null);
  readonly sessionUser = computed(() => this.authStore.user());
  readonly currentBusiness = computed(() => this.me()?.currentBusiness ?? this.authStore.business());
  readonly profileName = computed(() => this.me()?.user.displayName || this.sessionUser()?.name || 'Usuário');
  readonly profileEmail = computed(() => this.me()?.user.email || this.sessionUser()?.email || '');
  readonly profileAvatarUrl = computed(() => this.me()?.user.avatarUrl || this.sessionUser()?.avatarUrl || '');
  readonly profileInitials = computed(() => buildInitials(this.profileName()));

  readonly profileForm = this.fb.nonNullable.group({
    displayName: ['', [Validators.required, Validators.maxLength(120)]],
    avatarUrl: ['', [Validators.maxLength(2048)]],
  });

  constructor() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.api
      .getMe()
      .pipe(switchMap((me) => this.resolveProfileAvatar(me)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (me) => {
          this.me.set(me);
          this.profileForm.patchValue({
            displayName: me.user.displayName,
            avatarUrl: me.user.avatarUrl ?? '',
          });
          this.loading.set(false);
        },
        error: () => {
          const user = this.sessionUser();
          if (user) {
            this.profileForm.patchValue({
              displayName: user.name,
              avatarUrl: user.avatarUrl ?? '',
            });
          }
          this.loading.set(false);
          this.toastr.error('Não foi possível carregar o perfil completo.');
        },
      });
  }

  startEditingName() {
    this.profileForm.controls.displayName.setValue(this.profileName());
    this.editingName.set(true);
  }

  cancelEditingName() {
    this.profileForm.controls.displayName.setValue(this.profileName());
    this.editingName.set(false);
  }

  saveName() {
    if (this.profileForm.controls.displayName.invalid) return;

    const displayName = this.profileForm.controls.displayName.value.trim();
    if (!displayName) return;

    this.saving.set(true);
    this.api
      .updateMe({
        displayName,
        avatarUrl: this.profileAvatarUrl() || null,
      })
      .pipe(switchMap((user) => this.resolveUserAvatar(user)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.authStore.updateCurrentUser({
            name: user.displayName,
            avatarUrl: user.avatarUrl ?? null,
          });
          this.me.update((current) => (current ? { ...current, user } : current));
          this.profileForm.patchValue({
            displayName: user.displayName,
            avatarUrl: user.avatarUrl ?? '',
          });
          this.saving.set(false);
          this.editingName.set(false);
          this.toastr.success('Nome atualizado.');
        },
        error: () => {
          this.saving.set(false);
          this.toastr.error('Não foi possível atualizar o nome.');
        },
      });
  }

  requestPasswordReset() {
    const email = this.me()?.user.email ?? this.sessionUser()?.email;
    if (!email) {
      this.toastr.error('Não foi possível identificar o e-mail da conta.');
      return;
    }

    this.requestingPasswordReset.set(true);
    this.authStore
      .requestPasswordReset({ email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.requestingPasswordReset.set(false);
          this.toastr.success('Enviamos as instruções de redefinição para seu e-mail.');
        },
        error: () => {
          this.requestingPasswordReset.set(false);
          this.toastr.error('Não foi possível solicitar a redefinição agora.');
        },
      });
  }

  async disassociateBusiness(business: CoreMeBusiness) {
    const membershipId = business.membership?.id;
    if (!membershipId) return;

    if (business.id === this.currentBusiness()?.id) {
      this.toastr.error('Alterne para outra organização antes de desassociar a organização atual.');
      return;
    }

    const confirmed = await this.confirm.confirm({
      title: 'Desassociar organização?',
      description: `Você perderá o acesso a ${business.name}. Esta ação precisa ser revertida por outro administrador da organização.`,
      confirmLabel: 'Desassociar',
      tone: 'danger',
    });
    if (!confirmed) return;

    this.api
      .removeMembership(business.id, membershipId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.me.update((current) =>
            current
              ? {
                  ...current,
                  businesses: current.businesses.filter((item) => item.id !== business.id),
                }
              : current,
          );
          this.toastr.success('Organização desassociada.');
        },
        error: () => this.toastr.error('Não foi possível desassociar a organização.'),
      });
  }

  switchBusiness(business: CoreMeBusiness) {
    const currentBusinessId = this.currentBusiness()?.id;
    if (business.id === currentBusinessId || business.membership?.status !== 'active') {
      return;
    }

    this.switchingBusinessId.set(business.id);
    this.api
      .switchBusiness(business.id)
      .pipe(
        tap(({ currentBusiness, currentMembership }) => {
          this.authStore.setCurrentBusiness(
            {
              id: currentBusiness.id,
              name: currentBusiness.name,
              status: currentBusiness.status,
              providerOrganizationId: currentBusiness.providerOrganizationId ?? null,
            },
            currentMembership.id,
          );
        }),
        switchMap(() => this.authStore.loadSession()),
        switchMap(() => this.api.getMe()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (me) => {
          this.me.set(me);
          this.switchingBusinessId.set(null);
          this.toastr.success('Business alternado.');
        },
        error: () => {
          this.switchingBusinessId.set(null);
          this.toastr.error('Não foi possível alternar o business.');
        },
      });
  }

  canSwitchBusiness(business: CoreMeBusiness) {
    return business.id !== this.currentBusiness()?.id && business.membership?.status === 'active' && this.switchingBusinessId() === null;
  }

  uploadAvatar(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) return;

    if (!AVATAR_CONTENT_TYPES.has(file.type)) {
      this.toastr.error('Envie uma imagem JPG, PNG, WebP ou GIF.');
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      this.toastr.error('O avatar deve ter até 2 MB.');
      return;
    }

    this.uploadingAvatar.set(true);
    this.filesApi
      .issueUploadUrl({
        module: 'other',
        contentType: file.type,
        filename: file.name,
        sizeBytes: file.size,
      })
      .pipe(
        switchMap((issued) =>
          from(fetch(issued.uploadUrl, { method: 'PUT', headers: issued.requiredHeaders, body: file })).pipe(
            switchMap((response) =>
              response.ok
                ? this.filesApi.confirmUpload(issued.fileId).pipe(map(() => issued.fileId))
                : throwError(() => new Error(`upload failed with status ${response.status}`)),
            ),
          ),
        ),
        switchMap((avatarFileId) => {
          return this.api.updateMe({
            displayName: this.profileForm.controls.displayName.value.trim(),
            avatarUrl: avatarFileId,
          });
        }),
        switchMap((user) => this.resolveUserAvatar(user)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (user) => {
          this.profileForm.controls.avatarUrl.setValue(user.avatarUrl ?? '');
          this.authStore.updateCurrentUser({
            name: user.displayName,
            avatarUrl: user.avatarUrl ?? null,
          });
          this.me.update((current) => (current ? { ...current, user } : current));
          this.uploadingAvatar.set(false);
          this.toastr.success('Avatar atualizado.');
        },
        error: () => {
          this.uploadingAvatar.set(false);
          this.toastr.error('Não foi possível enviar o avatar.');
        },
      });
  }

  private resolveProfileAvatar(me: CoreMeResponse) {
    return this.filesApi.resolveFileUrl(me.user.avatarUrl ?? null).pipe(
      map((avatarUrl) => ({
        ...me,
        user: {
          ...me.user,
          avatarUrl: avatarUrl ?? null,
        },
      })),
    );
  }

  private resolveUserAvatar(user: CoreMeResponse['user']) {
    if (!user.avatarUrl) {
      return of(user);
    }

    return this.filesApi.resolveFileUrl(user.avatarUrl).pipe(
      map((avatarUrl) => ({
        ...user,
        avatarUrl: avatarUrl ?? null,
      })),
    );
  }
}

function buildInitials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  return (parts.map((part) => part[0]).join('') || 'U').toLocaleUpperCase('pt-BR');
}
